import { styled, ButtonBase } from '@mui/material';
import type { CSSObject } from '@mui/material';
import { forwardRef } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';

import { UI_BUTTON_VARIANT } from './Button.constants';
import type { UiButtonProps, UiButtonVariant } from './Button.types';

/**
 * A simple button component built on top of Material-UI ButtonBase with custom styling.
 *
 * @remarks
 * For accessibility guidelines (disabled vs aria-disabled, aria-busy, accessible labels),
 * implementation examples, and best practices, see ../../README.md.
 *
 */

export const UiButton = forwardRef<HTMLButtonElement | null, UiButtonProps>(
  ({ children, ...rest }: UiButtonProps, ref) => {
    const { onClick, onKeyDown, ...restProps } = rest;

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      const button = e.currentTarget;

      if (button.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      onClick?.(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      const button = e.currentTarget;

      if (
        button.getAttribute('aria-disabled') === 'true' &&
        (e.key === 'Enter' || e.key === ' ' || e.code === 'Space')
      ) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      onKeyDown?.(e);
    };

    return (
      <StyledButton
        disableTouchRipple
        ref={ref}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...restProps}
      >
        {children}
      </StyledButton>
    );
  }
);

const StyledButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: UiButtonVariant }>(({ theme, variant }) => {
  const commonLayoutStyles: CSSObject = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4, 6),

    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 'max(44px, 100%)',
      height: 'max(44px, 100%)',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
    },
  };

  const commonBorderStyles: CSSObject = {
    border: theme.borders.borderWidth1,
    borderRadius: theme.borders.borderRadius1,
  };

  const commonTypographyStyles: CSSObject = {
    ...theme.typography.body1,
    fontWeight: theme.typography.fontWeightMedium,
  };

  const commonStyles: CSSObject = {
    cursor: 'pointer',
    transition: `
      background-color 150ms ease-in,
      border-color 150ms ease-in,
      color 150ms ease-in
    `,
  };

  const linkStyles: CSSObject = {
    borderColor: 'transparent',
    color: theme.tokens.textLinksTextButtonsDefault,
    '&:hover': {
      color: theme.tokens.textLinksTextButtonsHover,
    },
    '&:focus': {
      color: theme.tokens.textLinksTextButtonsPressed,
    },
    '&:disabled,&[aria-disabled="true"]': {
      color: theme.tokens.textLinksTextButtonsDisabled,
    },
  };

  const textStyles: CSSObject = {
    padding: 0,
    borderColor: 'transparent',
    color: theme.tokens.buttonOutlinedValueDefault,
    '&:focus-visible': {
      borderColor: theme.tokens.optionCardsBorderActive,
    },
    '&:disabled,&[aria-disabled="true"]': {
      color: theme.tokens.textLinksTextButtonsDisabled,
    },
  };

  const ContainedOutlinedCommonStyles: CSSObject = {
    ...commonLayoutStyles,
    ...commonBorderStyles,
    ...commonTypographyStyles,
    ...commonStyles,
  };

  switch (variant) {
    case UI_BUTTON_VARIANT.CONTAINED:
      return {
        ...ContainedOutlinedCommonStyles,
        color: theme.tokens.buttonContainedValueDefault,
        backgroundColor: theme.tokens.buttonContainedBackgroundDefault,
        borderColor: theme.tokens.buttonContainedBackgroundDefault,
        '&:hover': {
          backgroundColor: theme.tokens.buttonContainedBackgroundHover,
          borderColor: theme.tokens.buttonContainedBackgroundHover,
        },
        '&:focus': {
          backgroundColor: theme.tokens.buttonContainedBackgroundPressed,
          borderColor: theme.tokens.buttonContainedBackgroundPressed,
        },
        '&:disabled,&[aria-disabled="true"]': {
          backgroundColor: theme.tokens.buttonContainedBackgroundDisabled,
          color: theme.tokens.buttonContainedValueDisabled,
          borderColor: theme.tokens.buttonContainedBackgroundDisabled,
        },
        '&[aria-disabled="true"]:focus-visible': {
          borderColor: theme.tokens.buttonContainedValueDisabled,
        },
      };

    case UI_BUTTON_VARIANT.OUTLINED:
      return {
        ...ContainedOutlinedCommonStyles,
        backgroundColor: theme.tokens.buttonOutlinedBackgroundDefault,
        color: theme.tokens.buttonOutlinedValueDefault,
        borderColor: theme.tokens.buttonOutlinedBorderDefault,
        '&:hover': {
          backgroundColor: theme.tokens.buttonOutlinedBackgroundHover,
          color: theme.tokens.buttonOutlinedValueDefault,
          borderColor: theme.tokens.buttonOutlinedBorderHover,
        },
        '&:focus': {
          backgroundColor: theme.tokens.buttonOutlinedBackgroundHover,
          color: theme.tokens.buttonOutlinedValueDefault,
          borderColor: theme.tokens.buttonOutlinedBorderDefault,
        },
        '&:disabled,&[aria-disabled="true"]': {
          backgroundColor: theme.tokens.buttonOutlinedBackgroundDisabled,
          color: theme.tokens.buttonOutlinedValueDisabled,
          borderColor: theme.tokens.buttonOutlinedBorderDisabled,
        },
        '&[aria-disabled="true"]:focus-visible': {
          borderColor: theme.tokens.buttonContainedValueDisabled,
        },
      };

    case UI_BUTTON_VARIANT.LINK:
      return {
        ...ContainedOutlinedCommonStyles,
        ...linkStyles,
      };

    case UI_BUTTON_VARIANT.INLINE_LINK:
      return {
        ...ContainedOutlinedCommonStyles,
        ...linkStyles,
      };

    case UI_BUTTON_VARIANT.TEXT:
      return {
        ...ContainedOutlinedCommonStyles,
        ...textStyles,
      };

    case UI_BUTTON_VARIANT.INLINE_TEXT:
      return {
        ...ContainedOutlinedCommonStyles,
        ...textStyles,
      };

    case UI_BUTTON_VARIANT.TERTIARY:
      return {
        ...ContainedOutlinedCommonStyles,
        padding: theme.spacing(3, 6),
        border: theme.borders.borderWidth0,
        backgroundColor: theme.tokens.buttonSmallBackgroundDefault,
        ...theme.typography.body2,
        fontWeight: theme.typography.fontWeightRegular,
        color: theme.tokens.buttonSmallValueDefault,
        '&:hover, &:focus-visible': {
          backgroundColor: theme.tokens.buttonSmallBackgroundHover,
        },
        '&:active ': {
          backgroundColor: theme.tokens.buttonSmallBackgroundPressed,
        },
        '&:disabled,&[aria-disabled="true"]': {
          backgroundColor: theme.tokens.buttonContainedBackgroundDisabled,
          color: theme.tokens.buttonContainedValueDisabled,
        },
        '&[aria-disabled="true"]:focus-visible': {
          borderColor: theme.tokens.buttonContainedValueDisabled,
        },
      };
  }
});
